import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  adminListNewsletter, adminDeleteNewsletter, adminBulkDeleteNewsletter, exportNewsletterCsvUrl,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/landing/Logo";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Download, Search, ArrowLeft, Loader2, Mail, Trash2, ChevronLeft, ChevronRight, LogOut,
} from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

export default function AdminNewsletter() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListNewsletter({ q: q || undefined, page, page_size: 50 });
      setData(res);
    } catch {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const allOnPage = useMemo(() => data.items.map((s) => s.id), [data.items]);
  const allSelected = allOnPage.length > 0 && allOnPage.every((id) => selected.has(id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) allOnPage.forEach((id) => next.delete(id));
    else allOnPage.forEach((id) => next.add(id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const doDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      if (confirm.single) {
        await adminDeleteNewsletter(confirm.ids[0]);
        toast.success("Subscriber removed");
      } else {
        const res = await adminBulkDeleteNewsletter(confirm.ids);
        toast.success(`${res.deleted ?? confirm.ids.length} removed`);
      }
      setSelected(new Set());
      setConfirm(null);
      await load();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / (data.page_size || 50)));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-newsletter-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/admin")} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <Logo />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden sm:inline">Newsletter</span>
          </div>
          <div className="flex items-center gap-2">
            <a href={exportNewsletterCsvUrl()} download data-testid="newsletter-export-btn">
              <Button variant="outline" size="sm" className="rounded-full">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
              </Button>
            </a>
            <Button variant="outline" size="sm" onClick={() => { logout(); nav("/admin/login"); }} className="rounded-full" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Newsletter subscribers
            </h1>
            <p className="text-sm text-slate-500 mt-1">{data.total} active · sorted by newest</p>
          </div>
          <div className="relative w-72 max-w-full">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search by email…"
              className="pl-9 rounded-full bg-white"
              data-testid="newsletter-search"
            />
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          {selected.size > 0 && (
            <div className="flex items-center justify-between px-5 py-2.5 bg-blue-50 border-b border-blue-100" data-testid="newsletter-bulk-bar">
              <div className="text-sm font-semibold text-blue-900">{selected.size} selected</div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} className="text-blue-700 hover:bg-blue-100">
                  Clear
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="rounded-full"
                  onClick={() => setConfirm({ ids: Array.from(selected), single: false })}
                  data-testid="newsletter-bulk-delete"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete {selected.size}
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                      data-testid="newsletter-select-all"
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                  </th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Source</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && data.items.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-16 text-center text-slate-500" data-testid="newsletter-empty">
                    No subscribers yet.
                  </td></tr>
                )}
                {!loading && data.items.map((s) => (
                  <tr key={s.id} className={`hover:bg-slate-50 ${selected.has(s.id) ? "bg-blue-50/40" : ""}`} data-testid={`newsletter-row-${s.id}`}>
                    <td className="px-5 py-4">
                      <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleOne(s.id)}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                    </td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${s.email}`} className="font-semibold text-slate-900 hover:underline flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-slate-400" />
                        {s.email}
                      </a>
                    </td>
                    <td className="px-5 py-4 text-slate-600 capitalize">{s.source || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                        s.status === "unsubscribed"
                          ? "bg-rose-50 text-rose-700 border border-rose-100"
                          : "bg-emerald-50 text-emerald-700 border border-emerald-100"
                      }`}>
                        {s.status || "active"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{fmtDate(s.created_at)}</td>
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        onClick={() => setConfirm({ ids: [s.id], single: true, email: s.email })}
                        title="Remove subscriber"
                        data-testid={`newsletter-delete-${s.id}`}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.total > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50">
              <div className="text-xs text-slate-600">
                Showing <b>{(data.page - 1) * data.page_size + 1}</b>–
                <b>{Math.min(data.page * data.page_size, data.total)}</b> of <b>{data.total}</b>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-full h-8 w-8 p-0">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 text-xs font-semibold text-slate-700">{data.page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={data.page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-full h-8 w-8 p-0">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="newsletter-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.single ? "Remove this subscriber?" : `Remove ${confirm?.ids?.length || 0} subscribers?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.single ? (
                <>This will permanently remove <b>{confirm.email}</b> from the newsletter.</>
              ) : (
                <>This will permanently remove <b>{confirm?.ids?.length}</b> subscribers. This cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="newsletter-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="newsletter-delete-confirm" className="bg-rose-600 hover:bg-rose-700 text-white">
              {deleting ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
